import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import {
  Search,
  Filter,
  MoreHorizontal,
  Edit,
  Trash2,
  Eye,
  Users,
  Mail,
  Crown,
  Phone,
  MapPin,
  Calendar,
  DollarSign,
  TrendingUp,
  TrendingDown,
  ShoppingBag,
  Star,
  UserPlus,
  MessageSquare,
  Loader2,
  RefreshCw,
  AlertTriangle,
  Copy,
  Monitor,
  Smartphone,
  Apple,
  AppWindow,
  HelpCircle,
  Bell,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Badge } from '../components/ui/badge';
import { Pagination } from '../components/ui/pagination';
import { formatCurrency, formatDate, getInitials } from '../lib/utils';
import { formatDateTime } from '../lib/utils';
import { useCustomerStatistics } from '../hooks/useCustomerStatistics';
import { usePaginatedCustomers } from '../hooks/usePaginatedCustomers';
import { Customer } from '../types';
import CustomerSort from '../components/CustomerSort';
import { CustomerProfileModal } from '../components/CustomerProfileModal';
import { EditCustomerModal } from '../components/EditCustomerModal';
import { CustomerOrdersModal } from '../components/CustomerOrdersModal';
import { SendMessageModal } from '../components/SendMessageModal';
import { Textarea } from '../components/ui/textarea';
import { PushNotificationService } from '../services/pushNotificationService';
import { updateCustomer } from '../services/customerService';

// Function to get phone number from primary address
const getCustomerPhoneFromPrimaryAddress = (customer: Customer): string | null => {
  // Find the primary/default address
  const primaryAddress = customer.addresses?.find(addr => addr.isDefault);
  if (primaryAddress && primaryAddress.phoneNumber) {
    return primaryAddress.phoneNumber;
  }
  
  // No primary address or no phone in primary address
  return null;
};

// Function to get location from primary address
const getCustomerLocationFromPrimaryAddress = (customer: Customer): { state: string; country: string } | null => {
  // Find the primary/default address
  const primaryAddress = customer.addresses?.find(addr => addr.isDefault);
  if (primaryAddress && primaryAddress.state && primaryAddress.country) {
    return {
      state: primaryAddress.state,
      country: primaryAddress.country
    };
  }
  
  // No primary address with complete location data
  return null;
};

// Function to determine customer status based on real data
const getCustomerStatus = (customer: Customer): 'active' | 'inactive' => {
  // Check if customer has explicit isActive field
  if (typeof customer.isActive === 'boolean') {
    return customer.isActive ? 'active' : 'inactive';
  }
  
  // Default to inactive if no isActive field
  return 'inactive';
};

const getDeviceMeta = (customer: Customer) => {
  const info = customer.lastAccessDevice;
  const origins = customer.accessDeviceOrigins || info?.flags || {};
  const platform = info?.platform;
  const surface = info?.surface;

  // Only fall back to legacy origins when no surface info is present
  const fromApp = surface === 'mobile_app_webview' || (!surface && origins.mobileApp);
  const isMobileBrowser = surface === 'mobile_browser' || (!surface && origins.mobileBrowser);
  const isDesktop = surface === 'desktop_browser' || (!surface && origins.desktop);

  let Icon = HelpCircle;
  let label = 'Unknown device';

  if (fromApp) {
    Icon = Smartphone;
    label = platform === 'ios' ? 'iOS App' : platform === 'android' ? 'Android App' : 'Mobile App';
  } else if (isMobileBrowser) {
    Icon = Smartphone;
    label = platform === 'ios' ? 'iOS Browser' : platform === 'android' ? 'Android Browser' : 'Mobile Browser';
  } else if (isDesktop) {
    Icon = Monitor;
    label = platform === 'macos' ? 'macOS Desktop' : platform === 'windows' ? 'Windows Desktop' : 'Desktop Browser';
  } else if (platform === 'macos' || platform === 'ios') {
    Icon = Apple;
    label = platform === 'ios' ? 'iOS' : 'macOS';
  } else if (platform === 'android') {
    Icon = Smartphone;
    label = 'Android';
  }

  const recordedAt = info?.recordedAt ? new Date(info.recordedAt) : undefined;
  const details: string[] = [label];
  if (info?.appVersion) details.push(`v${info.appVersion}`);
  if (recordedAt) details.push(`seen ${formatDate(recordedAt)}`);

  return { Icon, label, recordedAt, appVersion: info?.appVersion, tooltip: details.join(' • ') };
};

// Dummy data for columns that don't exist in real customer data yet
const getDummyCustomerData = (customerId: string) => {
  // Generate consistent dummy data based on customer ID
  const seed = customerId.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  
  const orders = Math.floor((seed % 20) + 1);
  const baseSpent = (seed % 1000) + 100;
  const avgOrderValue = baseSpent / orders;
  
  // Generate consistent address data
  const cities = ['New York', 'Los Angeles', 'Chicago', 'Houston', 'Phoenix', 'Philadelphia', 'San Antonio', 'San Diego', 'Dallas', 'San Jose'];
  const states = ['NY', 'CA', 'IL', 'TX', 'AZ', 'PA', 'TX', 'CA', 'TX', 'CA'];
  const cityIndex = seed % cities.length;
  
  // Calculate last order date (within last 180 days)
  const daysAgo = (seed % 180) + 1;
  const lastOrderDate = new Date();
  lastOrderDate.setDate(lastOrderDate.getDate() - daysAgo);
  
  // Calculate registration date (between 6 months and 2 years ago)
  const regDaysAgo = (seed % 548) + 180; // 180 days to 728 days ago
  const registrationDate = new Date();
  registrationDate.setDate(registrationDate.getDate() - regDaysAgo);
  
  return {
    totalOrders: orders,
    totalSpent: baseSpent,
    averageOrderValue: avgOrderValue,
    lastOrderDate,
    registrationDate,
    address: {
      city: cities[cityIndex],
      state: states[cityIndex]
    }
  };
};

const customerStatuses = ['All', 'active', 'inactive'];
const rushdMembershipFilters = [
  { value: 'all', label: 'All' },
  { value: 'members', label: "Rush'd Members" },
  { value: 'non-members', label: 'Non-members' },
] as const;
const deviceFilters = [
  { value: 'all', label: 'All' },
  { value: 'desktop', label: 'Desktop' },
  { value: 'mobile_app', label: 'Mobile App' },
  { value: 'mobile_browser', label: 'Mobile Browser' },
  { value: 'unknown', label: 'Unknown' },
] as const;

export function Customers() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [showFilters, setShowFilters] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [customerToEdit, setCustomerToEdit] = useState<Customer | null>(null);
  const [showOrdersModal, setShowOrdersModal] = useState(false);
  const [customerForOrders, setCustomerForOrders] = useState<Customer | null>(null);
  const [showSendMessageModal, setShowSendMessageModal] = useState(false);
  const [customerForMessage, setCustomerForMessage] = useState<Customer | null>(null);
  const [showPushModal, setShowPushModal] = useState(false);
  const [pushCustomer, setPushCustomer] = useState<Customer | null>(null);
  const [pushTitle, setPushTitle] = useState('');
  const [pushBody, setPushBody] = useState('');
  const [pushSending, setPushSending] = useState(false);

  // Get real customer statistics with trend data
  const { statistics, percentageChanges, loading: statisticsLoading, error: statisticsError, refresh: refreshStatistics } = useCustomerStatistics();

  // Use paginated customers hook
  const {
    customers,
    loading,
    error,
    totalCount,
    currentPage,
    totalPages,
    searchQuery,
    selectedStatus,
    rushdFilter,
    deviceFilter,
    sortField,
    sortOrder,
    isSearchMode,
    isFilterMode,
    isSortMode,
    isCombinedMode,
    searchLoading,
    filterLoading,
    sortLoading,
    setSelectedStatus,
    setRushdFilter,
    setDeviceFilter,
    setSorting,
    handleSearch,
    fetchPage,
    refresh,
    updateCustomerInCache,
    removeCustomerFromCache,
    invalidateCache,
  } = usePaginatedCustomers({ pageSize: 20 });

  // Handle URL search parameters from notifications
  useEffect(() => {
    const searchParam = searchParams.get('search');
    if (searchParam && searchParam !== searchQuery) {
      // Auto-trigger search with the parameter value
      handleSearch(searchParam);
      
      // Clear the URL parameter after triggering search to keep URL clean
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete('search');
      setSearchParams(newSearchParams, { replace: true });
    }
  }, [searchParams, searchQuery, handleSearch, setSearchParams]);

  // Handle copy customer ID
  const handleCopyCustomerId = async (customer: Customer) => {
    const customerId = customer.id;
    try {
      await navigator.clipboard.writeText(customerId);
      toast.success(`Customer ID ${customerId} copied to clipboard`);
    } catch (error) {
      console.error('Failed to copy customer ID:', error);
      toast.error('Failed to copy customer ID');
    }
  };

  // Handle refresh for both customers and statistics
  const handleRefresh = async () => {
    try {
      await Promise.all([refresh(), refreshStatistics()]);
      toast.success('Customer data refreshed successfully');
    } catch (error) {
      console.error('Error refreshing customer data:', error);
      toast.error('Failed to refresh customer data. Please try again.');
    }
  };

  // Handle view customer profile
  const handleViewProfile = (customer: Customer) => {
    setSelectedCustomer(customer);
    setShowProfileModal(true);
  };

  // Handle edit customer
  const handleEditCustomer = (customer: Customer) => {
    setCustomerToEdit(customer);
    setShowEditModal(true);
  };

  // Handle save customer (shared by both modals)
  const handleSaveCustomer = async (updatedCustomer: Customer) => {
    try {
      await updateCustomer(updatedCustomer.id, {
        name: updatedCustomer.name,
        isActive: updatedCustomer.isActive,
        addresses: updatedCustomer.addresses,
        notificationPreferences: updatedCustomer.notificationPreferences
      });
      toast.success('Customer updated successfully');
      
      if (isSearchMode || isFilterMode || isSortMode || isCombinedMode) {
        // Use cache updates when in any special mode
        updateCustomerInCache(updatedCustomer);
        // Always refresh statistics when customer data changes
        await refreshStatistics();
      } else {
        // For browse mode, do a full refresh (which includes statistics)
        await Promise.all([refresh(), refreshStatistics()]);
      }
      
      // Close whichever modal is open
      setShowEditModal(false);
    } catch (error) {
      console.error('Error updating customer:', error);
      toast.error('Failed to update customer. Please try again.');
      throw error; // Re-throw to let the modal handle the error state
    }
  };

  // Handle customer update from profile modal
  const handleCustomerUpdate = async (updatedCustomer: Customer) => {
    await handleSaveCustomer(updatedCustomer);
    // Update the selected customer for the profile modal
    setSelectedCustomer(updatedCustomer);
  };

  // Handle view customer orders
  const handleViewOrders = (customer: Customer) => {
    setCustomerForOrders(customer);
    setShowOrdersModal(true);
  };

  const handleSendMessage = (customer: Customer) => {
    setCustomerForMessage(customer);
    setShowSendMessageModal(true);
  };

  const handleSendPush = (customer: Customer) => {
    if (!customer.notificationTokens || customer.notificationTokens.length === 0) {
      toast.error('This customer does not have push notifications enabled.');
      return;
    }
    setPushCustomer(customer);
    setPushTitle("JamPack'd update");
    setPushBody(`Hi ${customer.name || 'there'}, you have a new message waiting in your JamPack'd app.`);
    setShowPushModal(true);
  };

  const handleConfirmPush = async () => {
    if (!pushCustomer) return;

    const trimmedTitle = pushTitle.trim();
    const trimmedBody = pushBody.trim();

    if (!trimmedTitle || !trimmedBody) {
      toast.error('Title and body are required.');
      return;
    }

    try {
      setPushSending(true);
      const result = await PushNotificationService.sendUserPush({
        userId: pushCustomer.id,
        title: trimmedTitle,
        body: trimmedBody,
        data: { customerId: pushCustomer.id },
      });

      toast.success(
        `Push sent${result?.targetedTokens ? ` to ${result.targetedTokens} token${result.targetedTokens === 1 ? '' : 's'}` : ''}`
      );
      setShowPushModal(false);
      setPushCustomer(null);
    } catch (error) {
      console.error('Failed to send push notification:', error);
      toast.error('Failed to send push notification');
    } finally {
      setPushSending(false);
    }
  };

  const getStatusConfig = (status: string) => {
    switch (status) {
      case 'active':
        return { color: 'bg-green-100 text-green-800', label: 'Active' };
      case 'inactive':
        return { color: 'bg-gray-100 text-gray-800', label: 'Inactive' };
      default:
        return { color: 'bg-gray-100 text-gray-800', label: 'Unknown' };
    }
  };

  const getTierConfig = (tier: string) => {
    switch (tier) {
      case 'bronze':
        return { color: 'bg-orange-100 text-orange-800', label: 'Bronze' };
      case 'silver':
        return { color: 'bg-gray-100 text-gray-800', label: 'Silver' };
      case 'gold':
        return { color: 'bg-yellow-100 text-yellow-800', label: 'Gold' };
      case 'platinum':
        return { color: 'bg-purple-100 text-purple-800', label: 'Platinum' };
      default:
        return { color: 'bg-gray-100 text-gray-800', label: 'Unknown' };
    }
  };

  const activeFilterCount = 
    (selectedStatus !== 'All' ? 1 : 0) + 
    (rushdFilter !== 'all' ? 1 : 0) +
    (deviceFilter !== 'all' ? 1 : 0);

  // Show error toasts when errors occur
  useEffect(() => {
    if (error) {
      toast.error(error);
    }
  }, [error]);

  // Show error toasts for customer statistics errors
  useEffect(() => {
    if (statisticsError) {
      toast.error('Failed to load customer statistics');
    }
  }, [statisticsError]);

  // Handle page change with auto-scroll
  const handlePageChange = (page: number) => {
    fetchPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Auto-scroll to top when page changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentPage]);

  return (
    <div className="space-y-6">
      {/* Action Buttons */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="flex justify-start items-center"
      >
        <div className="flex gap-3">
          <Button
            variant="outline"
            onClick={handleRefresh}
            disabled={loading}
            className="flex items-center gap-2"
          >
            {loading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4" />
            )}
            Refresh
        </Button>
        </div>
      </motion.div>

      {/* Stats Cards */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.1 }}
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
      >
        <Card className="admin-card">
          <CardContent className="p-6 flex flex-col h-full">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Customers</p>
                {statisticsLoading ? (
                  <div className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin text-jamaica-green" />
                    <span className="text-sm text-gray-500">Loading...</span>
                  </div>
                ) : statisticsError ? (
                  <p className="text-2xl font-bold text-red-600">Error</p>
                ) : (
                  <p className="text-2xl font-bold text-gray-900">{statistics?.totalCustomers ?? 0}</p>
                )}
              </div>
              <div className="h-12 w-12 bg-jamaica-green/10 rounded-lg flex items-center justify-center">
                <Users className="h-6 w-6 text-jamaica-green" />
              </div>
            </div>
            <div className="flex items-center mt-auto pt-4 text-sm">
              {statisticsLoading ? (
                <span className="text-gray-500">Loading trend...</span>
              ) : statisticsError ? (
                <span className="text-red-600">Error loading trend</span>
              ) : percentageChanges?.totalCustomers !== undefined && percentageChanges.totalCustomers !== 0 ? (
                <>
                  <TrendingUp className={`h-4 w-4 mr-1 ${percentageChanges.totalCustomers >= 0 ? 'text-green-500' : 'text-red-500'}`} />
                  <span className={percentageChanges.totalCustomers >= 0 ? 'text-green-600' : 'text-red-600'}>
                    {percentageChanges.totalCustomers >= 0 ? '+' : ''}{percentageChanges.totalCustomers.toFixed(1)}% from last month
                  </span>
                </>
              ) : (
                <span className="text-gray-600">No previous data for comparison</span>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="admin-card">
          <CardContent className="p-6 flex flex-col h-full">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Active Customers</p>
                {statisticsLoading ? (
                  <div className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin text-green-600" />
                    <span className="text-sm text-gray-500">Loading...</span>
                  </div>
                ) : statisticsError ? (
                  <p className="text-2xl font-bold text-red-600">Error</p>
                ) : (
                  <p className="text-2xl font-bold text-green-600">{statistics?.activeCustomers ?? 0}</p>
                )}
              </div>
              <div className="h-12 w-12 bg-green-100 rounded-lg flex items-center justify-center">
                <Star className="h-6 w-6 text-green-600" />
              </div>
            </div>
            <div className="flex items-center mt-auto pt-4 text-sm">
              {statisticsLoading ? (
                <span className="text-gray-500">Loading percentage...</span>
              ) : statisticsError ? (
                <span className="text-red-600">Error loading data</span>
              ) : (
                <span className="text-gray-600">
                  {statistics?.totalCustomers && statistics.totalCustomers > 0 && statistics?.activeCustomers !== null 
                    ? `${((statistics.activeCustomers / statistics.totalCustomers) * 100).toFixed(1)}% of total` 
                    : 'No data available'
                  }
                </span>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="admin-card">
          <CardContent className="p-6 flex flex-col h-full">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">New This Month</p>
                {statisticsLoading ? (
                  <div className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
                    <span className="text-sm text-gray-500">Loading...</span>
                  </div>
                ) : statisticsError ? (
                  <p className="text-2xl font-bold text-red-600">Error</p>
                ) : (
                  <p className="text-2xl font-bold text-blue-600">{statistics?.newCustomersThisMonth ?? 0}</p>
                )}
              </div>
              <div className="h-12 w-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <UserPlus className="h-6 w-6 text-blue-600" />
              </div>
            </div>
            <div className="flex items-center mt-auto pt-4 text-sm">
              {statisticsLoading ? (
                <span className="text-gray-500">Loading trend...</span>
              ) : statisticsError ? (
                <span className="text-red-600">Error loading trend</span>
              ) : percentageChanges?.newCustomersThisMonth !== undefined && percentageChanges.newCustomersThisMonth !== 0 ? (
                <>
                  {percentageChanges.newCustomersThisMonth >= 0 ? (
                    <TrendingUp className="h-4 w-4 mr-1 text-green-500" />
                  ) : (
                    <TrendingDown className="h-4 w-4 mr-1 text-red-500" />
                  )}
                  <span className={percentageChanges.newCustomersThisMonth >= 0 ? 'text-green-600' : 'text-red-600'}>
                    {percentageChanges.newCustomersThisMonth >= 0 ? '+' : ''}{percentageChanges.newCustomersThisMonth.toFixed(1)}% from last month
                  </span>
                </>
              ) : (
                <span className="text-gray-600">No comparison data yet</span>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="admin-card">
          <CardContent className="p-6 flex flex-col h-full">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Customer LTV</p>
                {statisticsLoading ? (
                  <div className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin text-jamaica-yellow" />
                    <span className="text-sm text-gray-500">Loading...</span>
                  </div>
                ) : statisticsError ? (
                  <p className="text-2xl font-bold text-red-600">Error</p>
                ) : statistics?.averageLTV && statistics.averageLTV > 0 ? (
                  <p className="text-2xl font-bold text-gray-900">{formatCurrency(statistics.averageLTV)}</p>
                ) : (
                  <div>
                    <p className="text-2xl font-bold text-gray-900">{formatCurrency(0)}</p>
                    <p className="text-xs text-gray-500 mt-1">LTV calculation pending</p>
                  </div>
                )}
              </div>
              <div className="h-12 w-12 bg-jamaica-yellow/20 rounded-lg flex items-center justify-center">
                <DollarSign className="h-6 w-6 text-jamaica-yellow" />
              </div>
            </div>
            <div className="flex items-center mt-auto pt-4 text-sm">
              {statisticsLoading ? (
                <span className="text-gray-500">Loading trend...</span>
              ) : statisticsError ? (
                <span className="text-red-600">Error loading trend</span>
              ) : percentageChanges?.averageLTV !== undefined && percentageChanges.averageLTV !== 0 ? (
                <>
                  {percentageChanges.averageLTV >= 0 ? (
                    <TrendingUp className="h-4 w-4 mr-1 text-green-500" />
                  ) : (
                    <TrendingDown className="h-4 w-4 mr-1 text-red-500" />
                  )}
                  <span className={percentageChanges.averageLTV >= 0 ? 'text-green-600' : 'text-red-600'}>
                    {percentageChanges.averageLTV >= 0 ? '+' : ''}{percentageChanges.averageLTV.toFixed(1)}% from last month
                  </span>
                </>
              ) : (
                <span className="text-gray-600">No comparison data yet</span>
              )}
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Filters and Search */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.2 }}
        className="bg-white rounded-xl shadow-md p-6 border border-gray-100"
      >
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <Input
                type="text"
                placeholder="Search customers by name, email, or phone..."
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                className="pl-10 bg-gray-50 border-0 focus:bg-white focus:ring-2 focus:ring-jamaica-green/20"
              />
              {(searchLoading || filterLoading || sortLoading) && (
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                  <Loader2 className="w-4 h-4 animate-spin text-jamaica-green" />
                </div>
              )}
            </div>
          </div>
          
          <div className="flex gap-3">
            <CustomerSort
              sortField={sortField}
              sortOrder={sortOrder}
              onSortChange={setSorting}
            />
            
            <Button
              variant={showFilters ? "default" : "outline"}
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center gap-2"
            >
              <Filter className="w-4 h-4" />
              Filters
              {activeFilterCount > 0 && (
                <Badge className={`ml-1 border-transparent ${showFilters ? 'bg-white text-jamaica-green hover:bg-white hover:text-jamaica-green' : 'bg-jamaica-green text-white hover:bg-jamaica-green hover:text-white'}`}>
                  {activeFilterCount}
                </Badge>
              )}
            </Button>
          </div>
        </div>

        {/* Filters */}
        {showFilters && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="mt-4 pt-4 border-t border-gray-200 space-y-4"
          >
            <div className="flex flex-wrap gap-2">
              <span className="text-sm font-medium text-gray-700 mr-2">Status:</span>
              {customerStatuses.map((status) => (
                <Button
                  key={status}
                  variant={selectedStatus === status ? "default" : "outline"}
                  size="sm"
                  onClick={() => setSelectedStatus(status)}
                  className={selectedStatus === status ? "bg-jamaica-green hover:bg-jamaica-green/90" : ""}
                >
                  {status === 'All' ? 'All' : getStatusConfig(status).label}
                </Button>
              ))}
            </div>

            <div className="flex flex-wrap gap-2">
              <span className="text-sm font-medium text-gray-700 mr-2">Rush'd:</span>
              {rushdMembershipFilters.map((filterOption) => (
                <Button
                  key={filterOption.value}
                  variant={rushdFilter === filterOption.value ? "default" : "outline"}
                  size="sm"
                  onClick={() => setRushdFilter(filterOption.value)}
                  className={rushdFilter === filterOption.value ? "bg-amber-500 hover:bg-amber-500/90 text-white" : ""}
                >
                  {filterOption.label}
                </Button>
              ))}
            </div>
            
            <div className="flex flex-wrap gap-2">
              <span className="text-sm font-medium text-gray-700 mr-2">Device:</span>
              {deviceFilters.map((filterOption) => (
                <Button
                  key={filterOption.value}
                  variant={deviceFilter === filterOption.value ? "default" : "outline"}
                  size="sm"
                  onClick={() => setDeviceFilter(filterOption.value)}
                  className={deviceFilter === filterOption.value ? "bg-gray-900 hover:bg-gray-900/90 text-white" : ""}
                >
                  {filterOption.label}
                </Button>
              ))}
            </div>
            
            {/* Clear Filters */}
            {activeFilterCount > 0 && (
              <div className="pt-2 border-t border-gray-100">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setSelectedStatus('All');
                    setRushdFilter('all');
                    setDeviceFilter('all');
                  }}
                  className="text-jamaica-green hover:text-jamaica-green/90 hover:bg-jamaica-green/10"
                >
                  Clear All Filters
                </Button>
            </div>
            )}
          </motion.div>
        )}
      </motion.div>

      {/* Customers Table */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.3 }}
      >
        <Card className="admin-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5 text-jamaica-green" />
              Customers ({totalCount})
              {isCombinedMode && (
                <Badge variant="outline" className="ml-2 text-jamaica-black border-jamaica-black hover:bg-transparent hover:text-jamaica-black hover:border-jamaica-black">
                  Combined Mode
                </Badge>
              )}
              {isSearchMode && !isCombinedMode && (
                <Badge variant="outline" className="ml-2 text-jamaica-green border-jamaica-green hover:bg-transparent hover:text-jamaica-green hover:border-jamaica-green">
                  Search Mode
                </Badge>
              )}
              {isFilterMode && !isCombinedMode && (
                <Badge variant="outline" className="ml-2 text-jamaica-yellow border-jamaica-yellow hover:bg-transparent hover:text-jamaica-yellow hover:border-jamaica-yellow">
                  Filter Mode
                </Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {error ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <AlertTriangle className="h-12 w-12 text-red-500 mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Error Loading Customers</h3>
                <p className="text-gray-600 mb-4">{error}</p>
                <Button onClick={handleRefresh} variant="outline">
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Try Again
                </Button>
              </div>
            ) : loading || searchLoading || filterLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-jamaica-green" />
                <span className="ml-2 text-gray-600">
                  {searchLoading ? 'Searching customers...' : 
                   filterLoading ? 'Filtering customers...' : 
                   sortLoading ? 'Sorting customers...' :
                   'Loading customers...'}
                </span>
              </div>
            ) : customers.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <Users className="h-12 w-12 text-gray-400 mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No Customers Found</h3>
                <p className="text-gray-600 mb-4">
                  {isCombinedMode 
                    ? 'No customers match your search query, filters, and sorting.'
                    : isSearchMode 
                    ? 'No customers match your search criteria.'
                    : isFilterMode
                    ? 'No customers match the selected filters.'
                    : isSortMode
                    ? 'No customers found for the current sorting.'
                    : 'No customers have been registered yet.'}
                </p>
                {(searchQuery || isFilterMode || sortField) && (
                  <div className="space-y-2">
                    {searchQuery && (
                      <Button onClick={() => handleSearch('')} variant="outline">
                        Clear Search
                      </Button>
                    )}
                    {(isFilterMode || sortField) && (
                      <Button onClick={() => {
                        setSelectedStatus('All');
                        setRushdFilter('all');
                        setSorting(null, 'desc');
                      }} variant="outline">
                        Clear Filters & Sort
                      </Button>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead>Customer</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead>Orders</TableHead>
                    <TableHead>Total Spent</TableHead>
                    <TableHead>Status</TableHead>
                        {/* <TableHead>Loyalty Tier</TableHead> */}
                    <TableHead>Last Order</TableHead>
                    <TableHead>Device</TableHead>
                    <TableHead className="w-16"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                      {customers.map((customer, index) => {
                        // Combine real customer data with dummy data for missing columns
                        const dummyData = getDummyCustomerData(customer.id);
                        const customerStatus = getCustomerStatus(customer);
                        const statusConfig = getStatusConfig(customerStatus);
                        const hasPushTokens = !!(customer.notificationTokens && customer.notificationTokens.length > 0);
                        const isRushdMember = !!customer.rushdMembership?.isMember;
                        const rushdRowClassName = isRushdMember
                          ? "hover:bg-amber-50 transition-all duration-300 border-l-4 border-amber-500 bg-gradient-to-r from-amber-50/80 to-white"
                          : "hover:bg-gray-50 transition-colors";
                        const rushdRowStyle = isRushdMember ? { boxShadow: '0 0 0 1px rgba(245, 158, 11, 0.25)' } : undefined;
                        const deviceMeta = getDeviceMeta(customer);
                        const DeviceIcon = deviceMeta.Icon;
                    
                    return (
                      <motion.tr
                        key={customer.id}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.3, delay: index * 0.05 }}
                        className={rushdRowClassName}
                        style={rushdRowStyle}
                      >
                        <TableCell>
                          <div className="flex items-center gap-3">
                            {isRushdMember && (
                              <div className="text-amber-500" title="JamPack'd Rush'd member">
                                <Crown className="w-5 h-5" />
                              </div>
                            )}
                            <div className="w-10 h-10 bg-gradient-to-br from-jamaica-green to-jamaica-green/80 rounded-full flex items-center justify-center text-white font-medium">
                                  {getInitials(customer.name || 'Unknown')}
                            </div>
                            <div className="space-y-1">
                                  <p className="font-medium text-gray-900">{customer.name || 'Unknown Customer'}</p>
                                  <p 
                                    className="text-xs text-gray-500 hover:text-jamaica-green cursor-pointer transition-colors break-all"
                                    onClick={() => handleCopyCustomerId(customer)}
                                    title="Click to copy customer ID"
                                  >
                                    ID: {customer.id.substring(0, 12)}...
                                  </p>
                                  {isRushdMember && (
                                    <span className="inline-flex items-center gap-1 text-xs font-semibold text-amber-700 bg-amber-50 rounded-full px-2 py-0.5">
                                      Rush'd Member
                                    </span>
                                  )}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="space-y-1">
                            <div className="flex items-center gap-2">
                              <Mail className="w-3 h-3 text-gray-400" />
                                  <p className="text-sm text-gray-600">{customer.email || 'No email'}</p>
                            </div>
                                {getCustomerPhoneFromPrimaryAddress(customer) && (
                            <div className="flex items-center gap-2">
                              <Phone className="w-3 h-3 text-gray-400" />
                                    <p className="text-sm text-gray-600">{getCustomerPhoneFromPrimaryAddress(customer)}</p>
                            </div>
                                )}
                                {getCustomerLocationFromPrimaryAddress(customer) && (
                            <div className="flex items-center gap-2">
                              <MapPin className="w-3 h-3 text-gray-400" />
                              <p className="text-xs text-gray-500">
                                      {getCustomerLocationFromPrimaryAddress(customer)?.state}, {getCustomerLocationFromPrimaryAddress(customer)?.country}
                              </p>
                            </div>
                                )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="space-y-1">
                            <div className="flex items-center gap-2">
                              <ShoppingBag className="w-4 h-4 text-gray-400" />
                                  <p className="font-medium text-gray-900">
                                    {customer.orderCount ?? customer.orderHistory.length}
                                  </p>
                            </div>
                            <p className="text-xs text-gray-500">
                                  Avg: {customer.averageOrderValue ? formatCurrency(customer.averageOrderValue) : 'No orders'}
                            </p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <p className="font-semibold text-gray-900">{formatCurrency(customer.totalSpent)}</p>
                        </TableCell>
                        <TableCell>
                          <Badge className={statusConfig.color}>
                            {statusConfig.label}
                          </Badge>
                        </TableCell>
                            {/* <TableCell>
                          <Badge className={tierConfig.color}>
                            {tierConfig.label}
                          </Badge>
                            </TableCell> */}
                        <TableCell>
                          <div className="space-y-1">
                            <div className="flex items-center gap-1">
                              <Calendar className="w-3 h-3 text-gray-400" />
                                  <p className="text-sm text-gray-600">
                                    {customer.lastOrderDate ? formatDate(customer.lastOrderDate) : 'No orders yet'}
                                  </p>
                            </div>
                          <p className="text-xs text-gray-500">
                                Member since {formatDate(customer.createdAt)}
                            </p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-col gap-0.5 text-gray-700" title={deviceMeta.tooltip}>
                            <div className="flex items-center gap-2">
                              <DeviceIcon className="w-4 h-4" />
                              <span className="text-xs text-gray-500">{deviceMeta.label}</span>
                            </div>
                            {customer.lastAccessDevice?.recordedAt && (
                              <span className="text-[11px] text-gray-400">
                                {formatDateTime(customer.lastAccessDevice.recordedAt)}
                              </span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm">
                                <MoreHorizontal className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => handleViewProfile(customer)}>
                                <Eye className="w-4 h-4 mr-2" />
                                View Profile
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleEditCustomer(customer)}>
                                <Edit className="w-4 h-4 mr-2" />
                                Edit Customer
                              </DropdownMenuItem>
                              {hasPushTokens && (
                                <DropdownMenuItem onClick={() => handleSendPush(customer)}>
                                  <Bell className="w-4 h-4 mr-2" />
                                  Send Push Notification
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuItem onClick={() => handleSendMessage(customer)}>
                                <MessageSquare className="w-4 h-4 mr-2" />
                                Send Message
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleViewOrders(customer)}>
                                <ShoppingBag className="w-4 h-4 mr-2" />
                                View Orders
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </motion.tr>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
                
                {/* Pagination */}
                <div className="p-6 border-t border-gray-200">
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={handlePageChange}
                    loading={loading}
                    totalItems={totalCount}
                    itemsPerPage={20}
                  />
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </motion.div>

      {/* Customer Profile Modal */}
      <CustomerProfileModal
        customer={selectedCustomer}
        isOpen={showProfileModal}
        onClose={() => setShowProfileModal(false)}
        onCustomerUpdate={handleCustomerUpdate}
        onViewOrders={handleViewOrders}
      />

      {/* Edit Customer Modal */}
      <EditCustomerModal
        customer={customerToEdit}
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        onSave={handleSaveCustomer}
      />

      {/* Customer Orders Modal */}
      <CustomerOrdersModal
        customer={customerForOrders}
        isOpen={showOrdersModal}
        onClose={() => setShowOrdersModal(false)}
      />

      {/* Send Message Modal */}
      <SendMessageModal
        customer={customerForMessage}
        isOpen={showSendMessageModal}
        onClose={() => setShowSendMessageModal(false)}
      />

      {/* Send Push Modal */}
      <Dialog
        open={showPushModal}
        onOpenChange={(open) => {
          setShowPushModal(open);
          if (!open) {
            setPushCustomer(null);
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Send push notification</DialogTitle>
            <DialogDescription>
              Send a direct push to {pushCustomer?.name || 'this customer'}. This option only appears for customers with push tokens from the mobile app.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-gray-700">Title</label>
              <Input
                value={pushTitle}
                onChange={(e) => setPushTitle(e.target.value)}
                placeholder="JamPack'd update"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-gray-700">Body</label>
              <Textarea
                value={pushBody}
                onChange={(e) => setPushBody(e.target.value)}
                placeholder="Hi there, you have a new message waiting in your JamPack'd app."
              />
            </div>
            {pushCustomer?.notificationTokens && (
              <p className="text-xs text-gray-500">
                Targeting {pushCustomer.notificationTokens.length} device{pushCustomer.notificationTokens.length === 1 ? '' : 's'} for this user.
              </p>
            )}
          </div>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setShowPushModal(false);
                setPushCustomer(null);
              }}
              disabled={pushSending}
            >
              Cancel
            </Button>
            <Button onClick={handleConfirmPush} disabled={pushSending}>
              {pushSending ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Sending...
                </span>
              ) : (
                'Send push'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
} 
